import * as THREE from "three";
import { INTERACTIVE_LAYER } from "./Raycaster.js";

/**
 * Owns the Three.js WebGL renderer, scene, and perspective camera.
 * Call init() once, then tick(delta) each frame from the game loop.
 */
export class SceneManager {
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;
  private readonly renderer: THREE.WebGLRenderer;

  constructor(canvas: HTMLCanvasElement) {
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x000000);

    this.camera = new THREE.PerspectiveCamera(
      75,
      canvas.clientWidth / canvas.clientHeight,
      0.1,
      100,
    );
    // Enemies / NPCs live on INTERACTIVE_LAYER so the raycaster can pick
    // them up for click-to-attack and click-to-talk. The camera still needs
    // to render that layer or the sprites stay invisible.
    this.camera.layers.enable(INTERACTIVE_LAYER);

    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: false });
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(canvas.clientWidth, canvas.clientHeight, false);

    window.addEventListener("resize", this.onResize);
  }

  tick(): void {
    this.renderer.render(this.scene, this.camera);
  }

  /**
   * GF5 — internal-resolution scale for the retro presentation mode.
   * 1 = native device pixels; ~0.35 renders chunky and upscales through
   * the canvas (pair with `image-rendering: pixelated` CSS).
   */
  setResolutionScale(scale: number): void {
    const ratio = Math.max(0.1, Math.min(1, scale)) * window.devicePixelRatio;
    this.renderer.setPixelRatio(ratio);
    const canvas = this.renderer.domElement;
    this.renderer.setSize(canvas.clientWidth, canvas.clientHeight, false);
  }

  dispose(): void {
    window.removeEventListener("resize", this.onResize);
    this.renderer.dispose();
  }

  private readonly onResize = (): void => {
    const canvas = this.renderer.domElement;
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
  };
}
